import { type ReactNode } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';

export interface ActiveFilter {
  key: string;
  label: string;
}

export interface FilterBarProps {
  /** Valeur de recherche globale. */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Contrôles de filtre (selects, etc.) rendus à droite de la recherche. */
  children?: ReactNode;
  /** Chips de filtres actifs. */
  activeFilters?: ActiveFilter[];
  onRemoveFilter?: (key: string) => void;
  onClearAll?: () => void;
  className?: string;
}

/**
 * Barre de filtres : recherche, filtres dynamiques, chips de filtres actifs,
 * bouton « Effacer tout ». Brief — composant #8.
 */
export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = 'Rechercher…',
  children,
  activeFilters = [],
  onRemoveFilter,
  onClearAll,
  className,
}: FilterBarProps) {
  const hasChips = activeFilters.length > 0;
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        {onSearchChange && (
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-muted" />
            <Input
              value={search ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="pl-9"
              aria-label="Recherche"
            />
          </div>
        )}
        {children}
      </div>

      {hasChips && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1 rounded-full bg-surface-hover px-2.5 py-1 text-xs font-medium text-content-secondary"
            >
              {f.label}
              {onRemoveFilter && (
                <button
                  onClick={() => onRemoveFilter(f.key)}
                  className="rounded-full p-0.5 hover:bg-border"
                  aria-label={`Retirer le filtre ${f.label}`}
                >
                  <X className="size-3" />
                </button>
              )}
            </span>
          ))}
          {onClearAll && (
            <button
              onClick={onClearAll}
              className="text-xs font-semibold text-brand-primary hover:underline"
            >
              Effacer tout
            </button>
          )}
        </div>
      )}
    </div>
  );
}
